import type { MouseEvent, ReactNode } from 'react';
import { router } from '../../../router';

const MIDDLE_BUTTON = 1;

/**
 * A link to a path the server chose, not one the route tree knows at compile time.
 *
 * Every other link in the app is a typed `<Link to="/organisation/positions/$id">`,
 * and should stay that way. A notification's deep link is different in kind: it is
 * *data*, built from the §L22 catalogue's pattern on the server, and the whole
 * point of non-negotiable #3 is that later slices add rows to that catalogue
 * without the browser learning about each one.
 *
 * Two things it has to get right, and neither is automatic:
 *
 * - **A real `href`.** Middle-click, Ctrl-click and "open in new tab" all have to
 *   work on a notification (06-qol §8), and they only work on an anchor with an
 *   address in it.
 * - **No full page load on an ordinary click.** `window.location` anywhere but a
 *   session boundary is a bug (CLAUDE.md §5a rule 7), so the plain-left-click case
 *   is intercepted and handed to the router. `router.history.push` takes a plain
 *   string, which is why it is used here rather than `navigate({ to })` — the typed
 *   API cannot describe a route decided at runtime, and forcing it would mean a
 *   type assertion for no extra safety.
 */
export function DeepLink({
  to,
  className,
  onNavigate,
  children,
}: {
  readonly to: string;
  readonly className?: string;
  readonly onNavigate?: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    // Opened either way, so it is read either way (Redesign Group 9) — a Ctrl-click that left the
    // row unread would keep nagging about something already open in another tab.
    onNavigate?.();
    // Let the browser do its own thing for anything that is not a plain left click.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    router.history.push(to);
  };

  /** A middle click opens a new tab and never reaches `onClick`. */
  const handleAuxClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (event.button === MIDDLE_BUTTON) onNavigate?.();
  };

  return (
    <a href={to} className={className} onClick={handleClick} onAuxClick={handleAuxClick}>
      {children}
    </a>
  );
}
